import { For, JSX, Match, Show, Switch, batch, createMemo } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useLogger } from '@trimble/solidjs-utils';
import { t } from '@trimble/i18n';
import {
  GeospatialFeature,
  PropertyDefinition,
  PropertyType,
  PropertyValue,
} from '@trimble/geospatial';

import { useFeatureExtractionContext } from '@mobile-mapping/providers';

const JSON_INDENT = 2;

type PropertyValues = Record<string, PropertyValue | undefined>;

function isPropertyValue(value: unknown): value is PropertyValue {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function toPropertyValues(
  properties: Readonly<Record<string, unknown>>
): PropertyValues {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => isPropertyValue(value))
  ) as PropertyValues;
}

export function FeaturePropertiesPanel(): JSX.Element {
  const featureExtraction = useFeatureExtractionContext();

  return (
    <div class="feature-extraction-properties" style={{ padding: '12px' }}>
      <Show
        when={featureExtraction.editingState.selectedFeature}
        keyed
        fallback={
          <div>
            {t('mobileMapping.cadEditing.propertiesPanel.noFeatureSelected')}
          </div>
        }
      >
        {(feature) => <FeatureProperties feature={feature} />}
      </Show>
    </div>
  );
}

/** Keyed remount on feature change reseeds drafts (no prop->signal sync). */
function FeatureProperties(props: { feature: GeospatialFeature }): JSX.Element {
  const featureExtraction = useFeatureExtractionContext();
  const logger = useLogger()
    .getLogger('FeatureExtraction')
    .createNamedLogger('PropertiesPanel');

  // The feature's own layer, not the currently-selected layer in the tree —
  // those diverge once another layer is picked for drawing while a feature
  // from a different layer stays selected.
  const featureLayer = createMemo(() =>
    featureExtraction.editingState.layers.find(
      (layer) => layer.id === props.feature.layerId
    )
  );

  const properties = createMemo<PropertyDefinition[]>(
    () => featureLayer()?.template?.properties.slice() ?? []
  );

  const [values, setValues] = createStore<PropertyValues>(
    // eslint-disable-next-line solid/reactivity -- intentional one-time seed; the keyed parent remounts on feature change
    toPropertyValues(props.feature.properties)
  );

  const commit = async (name: string, value: PropertyValue): Promise<void> => {
    const previousValue = values[name];
    setValues(name, value);
    const feature = props.feature;
    const featureId = feature.id;
    const scope = featureExtraction.scope();
    if (!featureId || !scope) {
      setValues(name, previousValue);
      return;
    }
    logger.info('Persisting property change', { id: featureId, name });
    try {
      await featureExtraction
        .featureRepository()
        .updateFeatureProperties(
          scope,
          featureId,
          feature.layerId,
          { [name]: value },
          featureExtraction.pointCloudCrs()
        );
      batch(() => {
        featureExtraction.mergeFeatureProperties(featureId, { [name]: value });
        featureExtraction.bumpTileCacheToken();
      });
    } catch (error) {
      logger.error(
        'Failed to persist property change',
        { id: featureId, name },
        error instanceof Error ? error : new Error(String(error))
      );
      setValues(name, previousValue);
    }
  };

  return (
    <>
      <section>
        <h4>{t('mobileMapping.cadEditing.propertiesPanel.generalSection')}</h4>
        <div>
          {t('mobileMapping.cadEditing.propertiesPanel.featureId', {
            id: props.feature.id ?? '(unsaved)',
          })}
        </div>
        <div>
          {t('mobileMapping.cadEditing.propertiesPanel.layer', {
            name: featureLayer()?.name ?? props.feature.layerId,
          })}
        </div>
      </section>

      <section>
        <h4>
          {t('mobileMapping.cadEditing.propertiesPanel.propertiesSection')}
        </h4>
        <Show
          when={properties().length > 0}
          fallback={
            <div>
              {t('mobileMapping.cadEditing.propertiesPanel.noSchemaFields')}
            </div>
          }
        >
          <For each={properties()}>
            {(property) => (
              <PropertyField
                property={property}
                value={values[property.name]}
                onCommit={(value) => void commit(property.name, value)}
              />
            )}
          </For>
        </Show>
      </section>

      <section>
        <h4>
          {t('mobileMapping.cadEditing.propertiesPanel.debugMetadataSection')}
        </h4>
        <pre style="max-height:200px;overflow:auto;font-size:11px;white-space:pre-wrap;">
          {JSON.stringify(props.feature.metadata, null, JSON_INDENT)}
        </pre>
      </section>
    </>
  );
}

interface PropertyFieldProps {
  property: PropertyDefinition;
  value: PropertyValue | undefined;
  onCommit(value: PropertyValue): void;
}

function PropertyField(props: PropertyFieldProps): JSX.Element {
  const label = (): string => props.property.displayName;
  const stringValue = (): string =>
    props.value === undefined || props.value === null
      ? ''
      : String(props.value);

  return (
    <label style="display:flex;flex-direction:column;gap:2px;margin-bottom:8px;">
      <span style="font-size:12px;">
        {label()}
        {props.property.required ? ' *' : ''}
      </span>
      <Switch
        fallback={
          <div style="font-size:12px;color:#b91c1c;">
            {t(
              'mobileMapping.cadEditing.propertiesPanel.unsupportedPropertyType',
              { type: props.property.type }
            )}
          </div>
        }
      >
        <Match when={props.property.type === PropertyType.Boolean}>
          <input
            type="checkbox"
            checked={Boolean(props.value)}
            disabled={props.property.readOnly}
            onChange={(e) => props.onCommit(e.currentTarget.checked)}
          />
        </Match>
        <Match when={props.property.type === PropertyType.Number}>
          <input
            type="number"
            value={stringValue()}
            disabled={props.property.readOnly}
            onChange={(e) => props.onCommit(Number(e.currentTarget.value))}
          />
        </Match>
        <Match when={props.property.type === PropertyType.Date}>
          <input
            type="date"
            value={stringValue()}
            disabled={props.property.readOnly}
            onChange={(e) => props.onCommit(e.currentTarget.value)}
          />
        </Match>
        <Match when={props.property.type === PropertyType.Choice}>
          <select
            value={stringValue()}
            disabled={props.property.readOnly}
            onChange={(e) => props.onCommit(e.currentTarget.value)}
          >
            <option value="" />
            <For each={props.property.choices ?? []}>
              {(choice) => <option value={choice.value}>{choice.label}</option>}
            </For>
          </select>
        </Match>
        <Match when={props.property.type === PropertyType.Text}>
          <input
            type="text"
            value={stringValue()}
            disabled={props.property.readOnly}
            onChange={(e) => props.onCommit(e.currentTarget.value)}
          />
        </Match>
      </Switch>
    </label>
  );
}
